import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import { getStoredToken } from '../../login/api/loginApi';
import { useMe } from '../../_shared/hooks/useMe';

interface AdminRouteGuardProps {
  children: ReactNode;
}

/**
 * ROLE_ADMIN 이상 전용 경로 보호.
 *
 * <p>인증 정보는 서버 `GET /api/eraf/auth/me` 응답을 신뢰원으로 사용한다.
 * 클라이언트에서 JWT 를 atob 으로 디코딩하지 않으며, role 값은 BE 가
 * `ROLE_ADMIN` 형태(prefix 포함)로 반환한다. 토큰은 localStorage 와
 * sessionStorage 양쪽에서 조회 (rememberMe=false 케이스 대응).
 *
 * <p>#405/#406 fix (UAT 2026-05-11): ROLE_EXEC(rank=3) ≥ ROLE_ADMIN(rank=2) 이므로
 * AdminRouteGuard 도 통과. 쓰기 버튼은 ButtonGuard / sandbox 423 가드가 별도 제한.
 */

/** 역할 서열 (높을수록 상위). ROLE_EXEC 는 ROLE_ADMIN 보다 상위. */
const ROLE_RANK: Record<string, number> = {
  ROLE_GUEST: -1,
  ROLE_USER: 0,
  ROLE_MANAGER: 1,
  ROLE_ADMIN: 2,
  ROLE_EXEC: 3,
};

export function AdminRouteGuard({ children }: AdminRouteGuardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = getStoredToken();
  const meQuery = useMe();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (meQuery.isLoading) {
    return null;
  }

  if (meQuery.isError || !meQuery.data) {
    return <Navigate to="/login" replace />;
  }

  const userRank = ROLE_RANK[meQuery.data.role] ?? 0;
  const requiredRank = ROLE_RANK['ROLE_ADMIN'];

  if (userRank < requiredRank) {
    return (
      <ErrorState
        title={t('gateway.shared.error.forbidden')}
        message={t('gateway.shared.error.forbiddenMessage')}
        actions={[
          {
            label: t('gateway.shared.error.actions.goHome'),
            onClick: () => navigate('/'),
            variant: 'primary',
          },
        ]}
      />
    );
  }

  return <>{children}</>;
}
